import { GraduationCap, Heart, BookOpen, Laptop, Users, Award, Compass } from 'lucide-react';

const TIMELINE = [
  {
    icon: GraduationCap,
    period: '2017 – 2021',
    title: "B.E. in Electronics and Communication",
    detail: 'MZCET, Pudukkottai',
  },
  {
    icon: Heart,
    period: '2021 – 2022',
    title: 'Career break',
    detail:
      "Stepped away from work to support my family during my father's medical treatment.",
  },
  {
    icon: BookOpen,
    period: '2022 – 2023',
    title: 'Exploring and upskilling',
    detail:
      'Learned web development from the ground up and explored a few different paths before deciding what fit me best.',
  },
  {
    icon: Laptop,
    period: 'Jan 2024 – Present',
    title: 'Freelance Web Developer',
    detail: 'Building React and Node applications for clients, end to end.',
  },
  {
    icon: Users,
    period: 'Sep 2024 – Present',
    title: 'Tech Mentor, GUVI Geek Network',
    detail: 'Teaching web development, DSA, and Python to students aged 7–17.',
  },
];

export default function About() {
  return (
    <section id="about" className="py-24 px-6 lg:px-8 bg-white">
      <div className="max-w-6xl mx-auto">
        <h2 className="font-display font-bold text-4xl sm:text-5xl text-slate-900 mb-4">
          About Me
        </h2>
        <p className="text-slate-500 text-lg max-w-2xl mb-16">
          The short version of how I got here — and where I'm headed next.
        </p>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Narrative */}
          <div className="space-y-6">
            <p className="text-lg text-slate-600 leading-relaxed">
              After finishing my B.E. in Electronics and Communication in
              2021, I took a career break to be there for my family during my
              father's medical treatment. It's a gap in my timeline, and I'd
              rather explain it plainly than leave it unexplained.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              When I was ready to get back to work, I returned to what I'd
              always been drawn to — building things and helping others learn
              to build too. Since then I've taken on freelance web
              development projects for real clients, and I currently work as
              a Tech Mentor at GUVI Geek Network, teaching web development,
              DSA, and Python.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              Now I'm looking for a full-time role where I can put that same
              energy into bigger, more ambitious projects — the kind that
              push my skills further and let me grow alongside a team.
            </p>

            {/* Achievement badge */}
            <div className="flex items-start gap-3 pt-4 mt-2 border-t border-slate-100">
              <div className="p-2.5 rounded-lg bg-violet-50 text-violet-600 flex-shrink-0">
                <Award size={20} />
              </div>
              <div>
                <p className="font-semibold text-slate-800">
                  Top 5 Finalist — MERNOVATION Hackathon
                </p>
                <p className="text-sm text-slate-500">GUVI, May 2024</p>
              </div>
            </div>

            {/* Beyond the code */}
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-lg bg-pink-50 text-pink-600 flex-shrink-0">
                <Compass size={20} />
              </div>
              <div>
                <p className="font-semibold text-slate-800">
                  Beyond the code
                </p>
                <p className="text-sm text-slate-500">
                  I've covered most of South India and am now working my way
                  through Central India. I'm also learning Bharatanatyam in
                  my free time.
                </p>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative pl-2">
            <div className="absolute left-[27px] top-2 bottom-2 w-px bg-slate-200" />
            <div className="space-y-10">
              {TIMELINE.map((item) => (
                <div key={item.title} className="relative flex gap-5">
                  <div className="relative z-10 flex-shrink-0 w-14 h-14 rounded-full bg-white border-2 border-violet-200 flex items-center justify-center text-violet-600">
                    <item.icon size={22} />
                  </div>
                  <div className="pt-2">
                    <p className="text-sm font-medium text-violet-600 mb-1">
                      {item.period}
                    </p>
                    <h3 className="font-display font-semibold text-lg text-slate-900 mb-1">
                      {item.title}
                    </h3>
                    <p className="text-slate-500 text-sm leading-relaxed">
                      {item.detail}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}